<template>
  <div>
    <page-header
      :title="$t('supplier_portal')"
      :subtitle="$t('supplier_portal_subtitle')"
      icon="mdi-truck-delivery"
      icon-color="primary"
    >
      <template #actions v-if="!device.isMobile">
        <circle-button
          :loading="loading"
          @click="load"
          icon="mdi-refresh"
          color="primary"
          :small="false"
        />
      </template>
    </page-header>

    <div class="px-4 mt-3">
      <v-tabs v-model="tab" background-color="primary" dark>
        <v-tab>
          <v-icon left>mdi-file-question-outline</v-icon>
          {{ $t('warehouse.request_quote') }}
        </v-tab>
        <v-tab>
          <v-icon left>mdi-cart-outline</v-icon>
          {{ $t('warehouse.purchase_order') }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab" class="mt-3">
        <!-- Request for Quote Tab -->
        <v-tab-item>
          <v-progress-linear v-if="loading" indeterminate color="primary" />
          <empty-state
            v-if="!loading && requestDocuments.length === 0"
            icon="mdi-file-question-outline"
            :title="$t('no_data')"
            :subtitle="$t('no_documents_for_supplier')"
          />
          <v-data-table
            v-else
            :headers="headers"
            :items="requestDocuments"
            :loading="loading"
            class="striped-table ordera-box-shadow elevation-1"
            :items-per-page="15"
          >
            <template v-slot:item.status="{ item }">
              <v-chip small :color="getStatusColor(item.status)" dark>
                {{ $t(statusKeyMap[item.status] || item.status) }}
              </v-chip>
            </template>
            <template v-slot:item.document_date="{ item }">
              {{ item.document_date | formatDate }}
            </template>
            <template v-slot:item.offer_deadline="{ item }">
              {{ item.offer_deadline | formatDate }}
            </template>
            <template v-slot:item.delivery_deadline="{ item }">
              {{ item.delivery_deadline | formatDate }}
            </template>
            <template v-slot:item.actions="{ item }">
              <circle-button
                icon="mdi-eye"
                :tooltip="$t('overview')"
                color="primary"
                @click="openDetail(item)"
              />
            </template>
          </v-data-table>
        </v-tab-item>

        <!-- Purchase Order Tab -->
        <v-tab-item>
          <v-progress-linear v-if="loading" indeterminate color="primary" />
          <empty-state
            v-if="!loading && orderDocuments.length === 0"
            icon="mdi-cart-outline"
            :title="$t('no_data')"
            :subtitle="$t('no_documents_for_supplier')"
          />
          <v-data-table
            v-else
            :headers="headers"
            :items="orderDocuments"
            :loading="loading"
            class="striped-table ordera-box-shadow elevation-1"
            :items-per-page="15"
          >
            <template v-slot:item.status="{ item }">
              <v-chip small :color="getStatusColor(item.status)" dark>
                {{ $t(statusKeyMap[item.status] || item.status) }}
              </v-chip>
            </template>
            <template v-slot:item.document_date="{ item }">
              {{ item.document_date | formatDate }}
            </template>
            <template v-slot:item.offer_deadline="{ item }">
              {{ item.offer_deadline | formatDate }}
            </template>
            <template v-slot:item.delivery_deadline="{ item }">
              {{ item.delivery_deadline | formatDate }}
            </template>
            <template v-slot:item.actions="{ item }">
              <circle-button
                icon="mdi-eye"
                :tooltip="$t('overview')"
                color="primary"
                @click="openDetail(item)"
              />
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>
    </div>

    <!-- Detail Dialog -->
    <standard-dialog
      v-model="detailDialog"
      :title="selectedDoc ? `${documentTypeLabel(selectedDoc.document_type)} #${selectedDoc.number || selectedDoc.id}` : ''"
      icon="mdi-file-document-outline"
      :show-save="false"
    >
      <template v-if="selectedDoc">
        <v-simple-table class="mb-4">
          <tbody>
            <tr>
              <td class="font-weight-bold">{{ $t('date') }}</td>
              <td>{{ selectedDoc.document_date | formatDate }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">{{ $t('status') }}</td>
              <td>
                <v-chip small :color="getStatusColor(selectedDoc.status)" dark>
                  {{ $t(statusKeyMap[selectedDoc.status] || selectedDoc.status) }}
                </v-chip>
              </td>
            </tr>
            <tr v-if="selectedDoc.offer_deadline">
              <td class="font-weight-bold">{{ $t('warehouse.offers_deadline') }}</td>
              <td>{{ selectedDoc.offer_deadline | formatDate }}</td>
            </tr>
            <tr v-if="selectedDoc.delivery_deadline">
              <td class="font-weight-bold">{{ $t('warehouse.delivery_deadline') }}</td>
              <td>{{ selectedDoc.delivery_deadline | formatDate }}</td>
            </tr>
            <tr v-if="selectedDoc.notes">
              <td class="font-weight-bold">{{ $t('note') }}</td>
              <td>{{ selectedDoc.notes }}</td>
            </tr>
          </tbody>
        </v-simple-table>

        <div class="subtitle-2 mb-2">{{ $t('warehouse.groceries') }}</div>
        <v-data-table
          :headers="groceryHeaders"
          :items="selectedDoc.items || []"
          hide-default-footer
          :items-per-page="-1"
          class="striped-table elevation-0"
          dense
        >
          <template v-slot:item.grocery_name="{ item }">
            {{ item.grocery ? item.grocery.name : '—' }}
          </template>
          <template v-slot:item.grocery_unit="{ item }">
            {{ item.grocery ? item.grocery.unit : '—' }}
          </template>
        </v-data-table>
      </template>
    </standard-dialog>
  </div>
</template>

<script>
import warehouseService from "@/services/warehouse";
import hasSnackbar from "@/mixins/hasSnackbar";
import PageHeader from "@/components/global/PageHeader.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import StandardDialog from "@/components/dialogs/StandardDialog.vue";

export default {
  name: "SupplierPortal",
  mixins: [hasSnackbar],
  components: {
    PageHeader,
    EmptyState,
    StandardDialog,
  },
  filters: {
    formatDate(val) {
      if (!val) return "—";
      return val.substring(0, 10);
    },
  },
  data() {
    return {
      tab: 0,
      loading: false,
      documents: [],
      detailDialog: false,
      selectedDoc: null,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await warehouseService.getSupplierDocuments();
        this.documents = res.data?.data ?? [];
      } catch (e) {
        this.showErrorSnackbar(this.$t("error"));
      } finally {
        this.loading = false;
      }
    },
    openDetail(item) {
      this.selectedDoc = item;
      this.detailDialog = true;
    },
    getStatusColor(status) {
      const map = {
        created: "blue",
        sent: "orange",
        confirm: "green",
        "confirm-half": "light-green",
        "reject-supplier": "red",
        reject: "red darken-2",
        delivered: "green darken-2",
        "delivered-half": "teal",
        closed: "grey",
      };
      return map[status] || "grey";
    },
    documentTypeLabel(type) {
      return this.documentTypes[type] || type;
    },
  },
  computed: {
    requestDocuments() {
      return this.documents.filter((d) => d.document_type === "request");
    },
    orderDocuments() {
      return this.documents.filter((d) => d.document_type === "order");
    },
    documentTypes() {
      return {
        order: this.$t("warehouse.purchase_order"),
        request: this.$t("warehouse.request_quote"),
      };
    },
    statusKeyMap() {
      return {
        created: "status_created",
        sent: "status_sent",
        confirm: "status_confirmed_by_supplier",
        "confirm-half": "status_partially_confirmed_by_supplier",
        "reject-supplier": "status_rejected_by_supplier",
        "changed-in-progress": "status_change_in_progress",
        "waiting-to-other-supplier": "status_waiting_for_another_supplier",
        reject: "status_rejected_by_us",
        delivered: "delivered_11",
        "delivered-half": "status_partially_delivered",
        "on-checking": "status_on_checking",
        "reject-on-arrived": "status_rejected_on_arrival",
        accept: "status_accepted",
        closed: "status_closed",
      };
    },
    headers() {
      return [
        { text: "ID", value: "id", width: "70px" },
        { text: this.$t("warehouse.document_number"), value: "number" },
        { text: this.$t("status"), value: "status" },
        { text: this.$t("date"), value: "document_date" },
        { text: this.$t("warehouse.offers_deadline"), value: "offer_deadline" },
        { text: this.$t("warehouse.delivery_deadline"), value: "delivery_deadline" },
        { text: this.$t("actions"), value: "actions", sortable: false, align: "right", width: "80px" },
      ];
    },
    groceryHeaders() {
      return [
        { text: this.$t("title"), value: "grocery_name" },
        { text: this.$t("q"), value: "amount" },
        { text: this.$t("unit_of_measurment"), value: "grocery_unit" },
        { text: this.$t("unit_price"), value: "unit_price" },
      ];
    },
  },
};
</script>
